import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { X, Download, Eye, Calendar, Clock, CheckCircle, XCircle, AlertCircle, Loader, Image, Hash, Search, Check, RefreshCw } from 'lucide-react'
import imageExportService from '../services/imageExportService'
import { toUtcDate } from '../../utils/dateFormat'

/**
 * AI Queue Dialog Component
 * Displays this template's AI image generation queue records (sample_type = 'ai').
 * Shows clear timestamps and provides search + status filtering.
 *
 * onApply({ pageIndex, itemIndex, url, jobId }) - apply a completed AI image to
 *   the element at the given position in the live template_content. When
 *   provided, completed jobs are auto-applied once (and an Apply button is shown
 *   for manual re-apply).
 */
const AiQueueDialog = ({
  isOpen,
  onClose,
  templateId,
  templateName,
  onApply,
  onRetry
}) => {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [appliedJobIds, setAppliedJobIds] = useState(() => new Set())
  // Remember which job ids we've already auto-applied so re-fetches don't
  // re-apply (and clobber) a value the user may have since changed.
  const autoAppliedRef = useRef(new Set())

  const fetchJobs = useCallback(async (refresh = false) => {
    if (!templateId) return
    setLoading(true)
    setError(null)

    try {
      const response = await imageExportService.getTemplateAiJobs(templateId, refresh)

      if (response && response.success !== false && Array.isArray(response.jobs)) {
        const sorted = [...response.jobs].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
        setJobs(sorted)
      } else {
        setJobs([])
        if (response && response.error) {
          setError('Failed to fetch queue records: ' + response.error)
        }
      }
    } catch (err) {
      console.error('Error fetching AI queue records:', err)
      setError('Failed to fetch queue records: ' + err.message)
    } finally {
      setLoading(false)
    }
  }, [templateId])

  useEffect(() => {
    if (isOpen && templateId) {
      fetchJobs()
    }
  }, [isOpen, templateId, fetchJobs])

  // Apply a single job's result to its element in the live template_content.
  const applyJob = useCallback((job) => {
    if (!onApply || !job || !job.output_url) return
    onApply({
      pageIndex: Number(job.page_index),
      itemIndex: Number(job.item_index),
      url: job.output_url,
      jobId: job.id,
    })
    setAppliedJobIds(prev => {
      const next = new Set(prev)
      next.add(job.id)
      return next
    })
  }, [onApply])

  const [retryingIds, setRetryingIds] = useState(() => new Set())

  // Retry a FAILED record: re-enqueue generation for its (page,item), then
  // refresh the list so the new attempt shows up.
  const retryJob = useCallback(async (job) => {
    if (!onRetry || !job || retryingIds.has(job.id)) return
    setRetryingIds(prev => new Set(prev).add(job.id))
    try {
      await onRetry(job)
      // Give the backend a beat to persist the new row, then refresh.
      await fetchJobs(false)
    } catch (err) {
      console.error('Retry failed:', err)
      setError('Retry failed: ' + (err?.message || 'unknown error'))
    } finally {
      setRetryingIds(prev => {
        const next = new Set(prev)
        next.delete(job.id)
        return next
      })
    }
  }, [onRetry, retryingIds, fetchJobs])

  // Task 2: auto-apply completed jobs once. When an item has both a generate
  // and a bg_removal job, the bg_removal (transparent) result wins — so we
  // resolve the best completed job per (page_index,item_index) and apply that.
  useEffect(() => {
    if (!onApply || !isOpen || jobs.length === 0) return

    const bestByItem = new Map()
    for (const j of jobs) {
      if (j.status !== 'completed' || !j.output_url) continue
      const key = `${j.page_index}:${j.item_index}`
      const current = bestByItem.get(key)
      // Prefer bg_removal over generate.
      if (!current || (j.phase === 'bg_removal' && current.phase !== 'bg_removal')) {
        bestByItem.set(key, j)
      }
    }

    let appliedAny = false
    for (const job of bestByItem.values()) {
      if (autoAppliedRef.current.has(job.id)) continue
      autoAppliedRef.current.add(job.id)
      onApply({
        pageIndex: Number(job.page_index),
        itemIndex: Number(job.item_index),
        url: job.output_url,
        jobId: job.id,
        auto: true,
      })
      appliedAny = true
    }
    if (appliedAny) {
      setAppliedJobIds(prev => {
        const next = new Set(prev)
        for (const job of bestByItem.values()) next.add(job.id)
        return next
      })
    }
  }, [jobs, onApply, isOpen])

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'queued':
      case 'picked':
      case 'preparing':
      case 'generating':
      case 'processing':
      case 'finalizing':
        return <Loader className="w-4 h-4 text-blue-500 animate-spin" />
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50'
      case 'failed':
        return 'text-red-600 bg-red-50'
      case 'queued':
      case 'picked':
      case 'preparing':
      case 'generating':
      case 'processing':
      case 'finalizing':
        return 'text-blue-600 bg-blue-50'
      default:
        return 'text-yellow-600 bg-yellow-50'
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return toUtcDate(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
  }

  const getPreviewUrl = (job) => job.output_url || null

  const handleDownload = (job) => {
    const url = getPreviewUrl(job)
    if (!url) return
    const link = document.createElement('a')
    link.href = url
    link.download = `ai-image-${job.id}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handlePreview = (job) => {
    const url = getPreviewUrl(job)
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  const availableStatuses = useMemo(
    () => [...new Set(jobs.map(j => j.status).filter(Boolean))],
    [jobs]
  )

  const filteredJobs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return jobs.filter(job => {
      if (statusFilter !== 'all' && job.status !== statusFilter) return false
      if (!term) return true
      const haystack = [
        String(job.id),
        job.job_id,
        job.status,
        job.phase,
        `page ${job.page_index + 1}`,
        `item ${job.item_index + 1}`,
        job.error_message,
        getPreviewUrl(job),
        formatDate(job.created_at)
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return haystack.includes(term)
    })
  }, [jobs, searchTerm, statusFilter])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">AI Generation Queue</h2>
            <p className="text-sm text-gray-600 mt-1">
              Template: {templateName || 'Unknown Template'} (ID: {templateId || 'undefined'})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by id, status, date, error..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All</option>
                {availableStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => fetchJobs(true)}
              className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
              title="Re-check outstanding jobs against the workflow queue"
            >
              Sync &amp; Refresh
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center space-x-2">
                <Loader className="w-5 h-5 animate-spin text-blue-500" />
                <span className="text-gray-600">Loading queue records...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <p className="text-red-600 font-medium">Error loading queue records</p>
                <p className="text-gray-600 text-sm mt-1">{error}</p>
                <button
                  onClick={() => fetchJobs(false)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">No AI queue records found</p>
                <p className="text-gray-500 text-sm mt-1">
                  {jobs.length === 0
                    ? 'No AI generation records for this template yet'
                    : 'No records match your search / filter'}
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-auto h-full">
              <div className="p-6">
                <div className="grid gap-4">
                  {filteredJobs.map((job) => {
                    const previewUrl = getPreviewUrl(job)
                    return (
                      <div
                        key={job.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex gap-4">
                          {/* Left: preview */}
                          <div className="flex-shrink-0">
                            <div className="w-28 h-28 bg-gray-100 rounded-lg border overflow-hidden flex items-center justify-center">
                              {previewUrl ? (
                                <>
                                  <img
                                    src={previewUrl}
                                    alt="AI preview"
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none'
                                      e.target.nextSibling.style.display = 'flex'
                                    }}
                                  />
                                  <div className="w-full h-full bg-gray-200 items-center justify-center text-gray-400 hidden">
                                    <Image className="w-8 h-8" />
                                  </div>
                                </>
                              ) : (
                                <Image className="w-8 h-8 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {/* Right: info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-3 mb-2">
                              {getStatusIcon(job.status)}
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                                {(job.status || 'unknown').toUpperCase()}
                              </span>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700">
                                {job.phase === 'bg_removal' ? 'BG REMOVAL' : 'GENERATE'}
                              </span>
                              <span className="text-sm text-gray-600">
                                Page {Number(job.page_index) + 1} · Item {Number(job.item_index) + 1}
                              </span>
                              {job.transparency && (
                                <span className="text-xs text-gray-500">transparent</span>
                              )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
                              <div className="flex items-center space-x-2">
                                <Calendar className="w-4 h-4" />
                                <span>Created: {formatDate(job.created_at)}</span>
                              </div>
                              {job.updated_at && job.updated_at !== job.created_at && (
                                <div className="flex items-center space-x-2">
                                  <Clock className="w-4 h-4" />
                                  <span>Updated: {formatDate(job.updated_at)}</span>
                                </div>
                              )}
                              <div className="flex items-center space-x-2 col-span-full">
                                <Hash className="w-3 h-3 text-gray-400" />
                                <span className="font-mono text-xs text-gray-500 truncate" title={job.job_id}>
                                  workflow job: {job.job_id}
                                </span>
                              </div>
                            </div>

                            {job.error_message && (
                              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                                <strong>Error:</strong> {job.error_message}
                              </div>
                            )}

                            {onRetry && job.status === 'failed' && (
                              <div className="flex items-center space-x-2 mb-1">
                                <button
                                  onClick={() => retryJob(job)}
                                  disabled={retryingIds.has(job.id)}
                                  className="px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed"
                                  title={`Retry generating Page ${Number(job.page_index) + 1} · Item ${Number(job.item_index) + 1}`}
                                >
                                  <RefreshCw className={`w-4 h-4 ${retryingIds.has(job.id) ? 'animate-spin' : ''}`} />
                                  {retryingIds.has(job.id) ? 'Retrying…' : 'Retry'}
                                </button>
                              </div>
                            )}

                            {previewUrl && (
                              <div className="flex items-center space-x-2">
                                {onApply && job.status === 'completed' && (
                                  <button
                                    onClick={() => applyJob(job)}
                                    className={`px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 text-sm font-medium ${
                                      appliedJobIds.has(job.id)
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                    }`}
                                    title={`Apply this image to Page ${Number(job.page_index) + 1} · Item ${Number(job.item_index) + 1}`}
                                  >
                                    <Check className="w-4 h-4" />
                                    {appliedJobIds.has(job.id) ? 'Applied — Re-apply' : 'Apply'}
                                  </button>
                                )}
                                <button
                                  onClick={() => handlePreview(job)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Preview in new tab"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDownload(job)}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Download"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filteredJobs.length} of {jobs.length} record{jobs.length !== 1 ? 's' : ''}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AiQueueDialog
